import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { suppliersAPI, filesAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { 
  ArrowLeft, Building2, Save, Upload, AlertCircle, 
  CheckCircle, Clock, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { SupplierPerformanceCard } from '@/components/SupplierPerformanceCard';

export default function SupplierReevaluationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  
  const [formData, setFormData] = useState({
    risk_assessment: '',
    production_capability: '',
    financial_capability: '',
    lead_time: '',
    counterfeit_prevention: '',
    product_process_criticality: '',
    product_process_criticality_other: '',
    customer_approval_required: '',
    customer_approval_reference: '',
    overall_risk_level: '',
    decision: 'approved',
    delivery_rating: '',
    quality_rating: '',
  });

  const isPurchaseManager = isAdmin || user?.app_roles?.supplier_mgmt === 'manager';
  const isQualityManager = isAdmin || user?.role === 'quality_manager';

  useEffect(() => {
    loadSupplier();
  }, [id]);

  const loadSupplier = async () => {
    try {
      const res = await suppliersAPI.get(id);
      setSupplier(res.data);
      
      // Pre-fill if there's existing evaluation data
      const purchaseEval = res.data.purchase_evaluation;
      const qualityEval = res.data.quality_evaluation;
      
      // If this is quality manager's turn, pre-fill with purchase manager's evaluation for reference
      if (res.data.reevaluation_status === 'pending_quality' && purchaseEval) {
        setFormData(prev => ({
          ...prev,
          risk_assessment: purchaseEval.risk_assessment || '',
          production_capability: purchaseEval.production_capability || '',
          financial_capability: purchaseEval.financial_capability || '',
          lead_time: purchaseEval.lead_time || '',
          counterfeit_prevention: purchaseEval.counterfeit_prevention || '',
          product_process_criticality: purchaseEval.product_process_criticality || '',
          product_process_criticality_other: purchaseEval.product_process_criticality_other || '',
          customer_approval_required: purchaseEval.customer_approval_required || '',
          customer_approval_reference: purchaseEval.customer_approval_reference || '',
          overall_risk_level: purchaseEval.overall_risk_level || '',
        }));
      }
    } catch (err) {
      toast.error('Failed to load supplier');
      navigate('/suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', 'supplier_evaluation');
      formData.append('entity_id', id);
      formData.append('file_category', 'evaluation_document');
      const res = await filesAPI.upload(formData);
      setUploadedDocs(prev => [...prev, res.data.id]);
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    }
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.risk_assessment || !formData.production_capability || 
        !formData.financial_capability || !formData.lead_time || !formData.counterfeit_prevention) {
      toast.error('Please fill in all evaluation fields');
      return;
    }
    if (!formData.product_process_criticality) {
      toast.error('Select the criticality of the product / process supplied');
      return;
    }
    if (formData.product_process_criticality === 'other' && !formData.product_process_criticality_other.trim()) {
      toast.error('Specify the criticality (Other)');
      return;
    }
    if (!formData.customer_approval_required) {
      toast.error('Indicate whether customer approval is required');
      return;
    }
    if (formData.customer_approval_required === 'yes' && !formData.customer_approval_reference.trim()) {
      toast.error('Provide the customer approval reference (e.g. Dassault-approved source)');
      return;
    }
    if (!formData.overall_risk_level) {
      toast.error('Select the overall supplier risk level');
      return;
    }
    
    // Check if this is first-time approval (no reevaluation history)
    const isFirstTimeApproval = !supplier.reevaluation_history || supplier.reevaluation_history.length === 0;
    
    // Quality Manager must provide ratings ONLY for reevaluation, not first-time approval
    if (supplier.reevaluation_status === 'pending_quality' && isQualityManager && !isFirstTimeApproval) {
      if (!formData.delivery_rating || !formData.quality_rating) {
        toast.error('Quality Manager must provide Delivery Rating and Quality Rating for reevaluation');
        return;
      }
      const dr = parseFloat(formData.delivery_rating);
      const qr = parseFloat(formData.quality_rating);
      if (isNaN(dr) || isNaN(qr) || dr < 0 || dr > 100 || qr < 0 || qr > 100) {
        toast.error('Ratings must be between 0 and 100');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        supplier_id: id,
        risk_assessment: formData.risk_assessment,
        production_capability: formData.production_capability,
        financial_capability: formData.financial_capability,
        lead_time: formData.lead_time,
        counterfeit_prevention: formData.counterfeit_prevention,
        product_process_criticality: formData.product_process_criticality,
        product_process_criticality_other: formData.product_process_criticality_other,
        customer_approval_required: formData.customer_approval_required,
        customer_approval_reference: formData.customer_approval_reference,
        overall_risk_level: formData.overall_risk_level,
        decision: formData.decision,
        documents: uploadedDocs,
      };
      
      // Include ratings only if provided (optional for first-time approval)
      if (supplier.reevaluation_status === 'pending_quality' && formData.delivery_rating && formData.quality_rating) {
        payload.delivery_rating = parseFloat(formData.delivery_rating);
        payload.quality_rating = parseFloat(formData.quality_rating);
      }
      
      await suppliersAPI.submitEvaluation(id, payload);
      toast.success('Evaluation submitted successfully');
      navigate('/suppliers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit evaluation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-400">Loading...</p>
      </div>
    );
  }

  if (!supplier) return null;

  const currentStatus = supplier.reevaluation_status;
  const canEvaluate = (currentStatus === 'pending_purchase' && isPurchaseManager) ||
                      (currentStatus === 'pending_quality' && isQualityManager);

  if (!canEvaluate) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/suppliers')}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-12 text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Cannot Evaluate</h2>
          <p className="text-slate-500 mt-2">
            {currentStatus === 'pending_purchase' 
              ? 'This supplier is pending Purchase Manager review.'
              : currentStatus === 'pending_quality'
              ? 'This supplier is pending Quality Manager review.'
              : 'This supplier is not currently under reevaluation.'}
          </p>
        </main>
      </div>
    );
  }

  const calculatedSR = formData.quality_rating && formData.delivery_rating
    ? (0.7 * parseFloat(formData.quality_rating) + 0.3 * parseFloat(formData.delivery_rating)).toFixed(2)
    : null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/suppliers')}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="h-4 w-px bg-slate-300" />
              <div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  <h1 className="text-lg font-semibold text-[#0F172A]">Supplier Reevaluation</h1>
                </div>
                <p className="text-xs text-slate-500">{supplier.supplier_name}</p>
              </div>
            </div>
            <Badge className={currentStatus === 'pending_purchase' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}>
              {currentStatus === 'pending_purchase' ? 'Purchase Manager Review' : 'Quality Manager Review'}
            </Badge>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Supplier Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Supplier Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500 text-xs">Supplier ID</p>
                <p className="font-mono">{supplier.supplier_id}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">SAP Code</p>
                <p>{supplier.supplier_code || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Location</p>
                <p>{supplier.city}, {supplier.country}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs">Previous Rating</p>
                <p className="font-semibold">{supplier.supplier_rating ? `SR: ${supplier.supplier_rating}` : 'N/A'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Past-year performance report (data-driven, for reference) */}
          <SupplierPerformanceCard
            supplierId={id}
            showQuality={currentStatus === 'pending_quality'}
          />

          {/* Previous Evaluation (for QM) */}
          {currentStatus === 'pending_quality' && supplier.purchase_evaluation && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  Purchase Manager Evaluation
                </CardTitle>
                <CardDescription className="text-xs">
                  Evaluated by {supplier.purchase_evaluation.evaluator_name} on {new Date(supplier.purchase_evaluation.evaluated_at).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div><strong>Decision:</strong> <Badge variant="outline">{supplier.purchase_evaluation.decision}</Badge></div>
              </CardContent>
            </Card>
          )}

          {/* Evaluation Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Evaluation Assessment</CardTitle>
              <CardDescription className="text-xs">Provide your assessment for each criterion</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Risk Assessment *</Label>
                <Textarea
                  value={formData.risk_assessment}
                  onChange={(e) => handleChange('risk_assessment', e.target.value)}
                  placeholder="Assess supplier risk factors..."
                  className="min-h-[80px]"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Production Capability *</Label>
                <Textarea
                  value={formData.production_capability}
                  onChange={(e) => handleChange('production_capability', e.target.value)}
                  placeholder="Evaluate production capacity and capability..."
                  className="min-h-[80px]"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Financial Capability *</Label>
                <Textarea
                  value={formData.financial_capability}
                  onChange={(e) => handleChange('financial_capability', e.target.value)}
                  placeholder="Assess financial stability and capability..."
                  className="min-h-[80px]"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Lead Time *</Label>
                <Textarea
                  value={formData.lead_time}
                  onChange={(e) => handleChange('lead_time', e.target.value)}
                  placeholder="Evaluate lead time performance..."
                  className="min-h-[80px]"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Counterfeit Parts Prevention *</Label>
                <Textarea
                  value={formData.counterfeit_prevention}
                  onChange={(e) => handleChange('counterfeit_prevention', e.target.value)}
                  placeholder="Assess counterfeit prevention measures..."
                  className="min-h-[80px]"
                  required
                />
              </div>

              <div className="space-y-1.5 border-t pt-4">
                <Label className="text-xs font-medium">Criticality of product / process supplied *</Label>
                <Select value={formData.product_process_criticality || undefined} onValueChange={(v) => handleChange('product_process_criticality', v)}>
                  <SelectTrigger className="h-9" data-testid="supplier-criticality-select"><SelectValue placeholder="Select criticality" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="special_process">Special process</SelectItem>
                    <SelectItem value="class1_pse">Class 1 / PSE part</SelectItem>
                    <SelectItem value="raw_material">Raw material</SelectItem>
                    <SelectItem value="standard_commercial">Standard or commercial item</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {formData.product_process_criticality === 'other' && (
                  <Input
                    value={formData.product_process_criticality_other}
                    onChange={(e) => handleChange('product_process_criticality_other', e.target.value)}
                    placeholder="Specify criticality"
                    className="h-9 mt-1.5"
                    data-testid="supplier-criticality-other"
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Customer approval required? *</Label>
                <Select value={formData.customer_approval_required || undefined} onValueChange={(v) => handleChange('customer_approval_required', v)}>
                  <SelectTrigger className="h-9" data-testid="supplier-customer-approval-select"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
                {formData.customer_approval_required === 'yes' && (
                  <Input
                    value={formData.customer_approval_reference}
                    onChange={(e) => handleChange('customer_approval_reference', e.target.value)}
                    placeholder="Reference — e.g. Dassault-approved source"
                    className="h-9 mt-1.5"
                    data-testid="supplier-customer-approval-ref"
                  />
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Overall supplier risk level *</Label>
                <Select value={formData.overall_risk_level || undefined} onValueChange={(v) => handleChange('overall_risk_level', v)}>
                  <SelectTrigger className="h-9" data-testid="supplier-risk-level-select"><SelectValue placeholder="Low / Medium / High" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
                {['medium', 'high'].includes(formData.overall_risk_level) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-800 mt-1.5" data-testid="supplier-risk-controls-note">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    For <strong>Medium / High</strong> risk, select controls before first order as per <strong>QSP-MT-840</strong>.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quality Manager Ratings */}
          {currentStatus === 'pending_quality' && isQualityManager && (
            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Star className="w-4 h-4 text-emerald-600" />
                  Performance Ratings
                </CardTitle>
                <CardDescription className="text-xs">
                  {(!supplier.reevaluation_history || supplier.reevaluation_history.length === 0)
                    ? 'Ratings are optional for first-time supplier approval (no transaction history yet)'
                    : 'Quality Manager must provide performance ratings (0-100)'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {(!supplier.reevaluation_history || supplier.reevaluation_history.length === 0) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-700">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    This is a <strong>first-time approval</strong>. Since this supplier has no transaction history, 
                    you can skip the ratings and approve based on qualification criteria. Ratings will be required 
                    during future reevaluations.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      Delivery Rating (0-100) {supplier.reevaluation_history?.length > 0 ? '*' : '(Optional)'}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.delivery_rating}
                      onChange={(e) => handleChange('delivery_rating', e.target.value)}
                      placeholder="0-100"
                      className="h-9"
                    />
                    <p className="text-[10px] text-slate-400">On-time delivery performance</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                      Quality Rating (0-100) {supplier.reevaluation_history?.length > 0 ? '*' : '(Optional)'}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.quality_rating}
                      onChange={(e) => handleChange('quality_rating', e.target.value)}
                      placeholder="0-100"
                      className="h-9"
                    />
                    <p className="text-[10px] text-slate-400">Quality acceptance rate</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Supplier Rating (SR)</Label>
                    <div className="h-9 px-3 flex items-center bg-emerald-50 border border-emerald-200 rounded-md">
                      <span className="text-lg font-bold text-emerald-700">
                        {calculatedSR || '—'}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400">SR = 70% Quality + 30% Delivery</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Decision */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Decision</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Your Decision *</Label>
                <Select value={formData.decision} onValueChange={(v) => handleChange('decision', v)}>
                  <SelectTrigger className="h-9 w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full" /> Approve
                      </span>
                    </SelectItem>
                    <SelectItem value="on_hold">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-amber-500 rounded-full" /> On Hold
                      </span>
                    </SelectItem>
                    <SelectItem value="no_longer_needed">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-red-500 rounded-full" /> No Longer Needed
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Document Upload */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Supporting Documents</Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={handleFileUpload}
                    data-testid="evaluation-file-upload"
                  />
                  <Button type="button" variant="outline" size="sm" className="text-xs gap-1">
                    <Upload className="w-3 h-3" /> Upload Document
                  </Button>
                </label>
                {uploadedDocs.length > 0 && (
                  <p className="text-xs text-emerald-600">{uploadedDocs.length} document(s) uploaded</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/suppliers')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Submitting...' : 'Submit Evaluation'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
